import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, TrendingUp, Users, DollarSign, Target, 
  BarChart3, PieChart, Calendar, Megaphone, Building2,
  CheckCircle2, XCircle, Clock
} from 'lucide-react';
import { useMarketingReports } from '../hooks';

export const MarketingReports = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  
  const { stats, loading, refetch } = useMarketingReports(appliedStartDate, appliedEndDate);

  const applyDateFilter = () => {
    setAppliedStartDate(startDate || null);
    setAppliedEndDate(endDate || null);
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStartDate(null);
    setAppliedEndDate(null);
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    return `₹${value.toLocaleString()}`;
  };

  const getSourceColor = (index) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500',
      'bg-pink-500', 'bg-indigo-500', 'bg-red-500', 'bg-orange-500', 'bg-teal-500'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8" data-testid="marketing-reports-page">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Link to="/marketing-leads">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Leads
                  </Button>
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <BarChart3 className="h-6 w-6 text-purple-600" />
                    Marketing Reports
                  </h1>
                  <p className="text-slate-500">Analyze your marketing campaign performance</p>
                </div>
              </div>
            </div>

            {/* Date Filter */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                    data-testid="report-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                    data-testid="report-end-date"
                  />
                </div>
                <Button onClick={applyDateFilter} data-testid="apply-date-filter">
                  Apply Filter
                </Button>
                {(appliedStartDate || appliedEndDate) && (
                  <Button variant="outline" onClick={clearDateFilter}>
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <Megaphone className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Leads</p>
                        <p className="text-2xl font-bold text-slate-900">{stats?.total_leads || 0}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Target className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Conversion Rate</p>
                        <p className="text-2xl font-bold text-green-600">{stats?.conversion_rate || 0}%</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <DollarSign className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Estimated Revenue</p>
                        <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.total_estimated_revenue)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-emerald-100 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Actual Revenue</p>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats?.total_actual_revenue)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <PieChart className="h-5 w-5 text-purple-600" />
                      Leads by Status
                    </h2>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">New</span>
                        </div>
                        <span className="text-lg font-bold text-blue-600">{stats?.leads_by_status?.new || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-yellow-600" />
                          <span className="font-medium">Contacted</span>
                        </div>
                        <span className="text-lg font-bold text-yellow-600">{stats?.leads_by_status?.contacted || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-purple-600" />
                          <span className="font-medium">Qualified</span>
                        </div>
                        <span className="text-lg font-bold text-purple-600">{stats?.leads_by_status?.qualified || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="font-medium">Won</span>
                        </div>
                        <span className="text-lg font-bold text-green-600">{stats?.won_leads || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="font-medium">Lost</span>
                        </div>
                        <span className="text-lg font-bold text-red-600">{stats?.lost_leads || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Leads by Source */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-purple-600" />
                      Leads by Source
                    </h2>
                    <div className="space-y-3">
                      {stats?.leads_by_source && Object.entries(stats.leads_by_source).map(([source, count], index) => (
                        <div key={source} className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${getSourceColor(index)}`}></div>
                          <span className="flex-1 text-slate-700">{source}</span>
                          <span className="font-bold text-slate-900">{count}</span>
                        </div>
                      ))}
                      {(!stats?.leads_by_source || Object.keys(stats.leads_by_source).length === 0) && (
                        <p className="text-slate-500 text-center py-4">No data available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Branch and Assignee Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Leads by Branch */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-purple-600" />
                      Performance by Branch
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs text-slate-500 uppercase">
                            <th className="pb-3">Branch</th>
                            <th className="pb-3 text-center">Total</th>
                            <th className="pb-3 text-center">Won</th>
                            <th className="pb-3 text-right">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {stats?.leads_by_branch?.map((branch) => (
                            <tr key={branch.branch_id}>
                              <td className="py-3 font-medium text-slate-900">{branch.branch_name}</td>
                              <td className="py-3 text-center text-slate-600">{branch.total}</td>
                              <td className="py-3 text-center text-green-600 font-medium">{branch.won}</td>
                              <td className="py-3 text-right text-slate-900 font-medium">{formatCurrency(branch.revenue)}</td>
                            </tr>
                          ))}
                          {(!stats?.leads_by_branch || stats.leads_by_branch.length === 0) && (
                            <tr>
                              <td colSpan="4" className="py-4 text-center text-slate-500">No data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Leads by Assignee */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-600" />
                      Performance by Team Member
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs text-slate-500 uppercase">
                            <th className="pb-3">Name</th>
                            <th className="pb-3 text-center">Total</th>
                            <th className="pb-3 text-center">Conv. Rate</th>
                            <th className="pb-3 text-right">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {stats?.leads_by_assignee?.map((assignee) => (
                            <tr key={assignee.assignee_id}>
                              <td className="py-3 font-medium text-slate-900">{assignee.assignee_name}</td>
                              <td className="py-3 text-center text-slate-600">{assignee.total}</td>
                              <td className="py-3 text-center">
                                <span className={`font-medium ${assignee.conversion_rate >= 30 ? 'text-green-600' : assignee.conversion_rate >= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {assignee.conversion_rate}%
                                </span>
                              </td>
                              <td className="py-3 text-right text-slate-900 font-medium">{formatCurrency(assignee.revenue)}</td>
                            </tr>
                          ))}
                          {(!stats?.leads_by_assignee || stats.leads_by_assignee.length === 0) && (
                            <tr>
                              <td colSpan="4" className="py-4 text-center text-slate-500">No data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Time-based Breakdown */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    Monthly Trend
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 uppercase">
                          <th className="pb-3">Month</th>
                          <th className="pb-3 text-center">Total Leads</th>
                          <th className="pb-3 text-center">Won</th>
                          <th className="pb-3 text-center">Lost</th>
                          <th className="pb-3 text-right">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {stats?.monthly_breakdown?.slice().reverse().map((month) => (
                          <tr key={month.period}>
                            <td className="py-3 font-medium text-slate-900">{month.period}</td>
                            <td className="py-3 text-center text-slate-600">{month.total}</td>
                            <td className="py-3 text-center text-green-600 font-medium">{month.won}</td>
                            <td className="py-3 text-center text-red-600">{month.lost}</td>
                            <td className="py-3 text-right text-slate-900 font-medium">{formatCurrency(month.revenue)}</td>
                          </tr>
                        ))}
                        {(!stats?.monthly_breakdown || stats.monthly_breakdown.length === 0) && (
                          <tr>
                            <td colSpan="5" className="py-4 text-center text-slate-500">No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
